import _ from 'underscore';
import Promise from 'bluebird';

import { priorityLevel } from './const';

export class PopupManager {
  constructor() {
    this.lock = [];
    this.waitList = [];
    this.gate = false;
    this.timer = null;
    this.logger = null;
    this.isCentralizedPopupManagementEnabled = false;
  }

  async acquireLock({
    key,
    wait = false,
    priority = priorityLevel.default,
  }) {
    if (!this.checkPermission()) {
      this.lock.push({ key, force: false });
      this.logPopupManagerState('Acquired lock without permission');
      return true;
    }

    if (!this.validateKey(key)) {
      return false;
    }

    // if gate is set, wait for the gate to be cleared
    if (this.getGate()) {
      return this.getWaitLockPromise(key, priority);
    }

    // if timer is set, wait for the timer to be cleared
    if (this.getTimer()) {
      return this.getWaitLockPromise(key, priority);
    }

    // acquire lock if lock is empty
    if (_.isEmpty(this.lock)) {
      this.lock.push({ key, force: false });
      this.logPopupManagerState('Acquired lock');
      return true;
    }

    // if lock is not empty, wait for the lock to be released
    if (wait) {
      return this.getWaitLockPromise(key, priority);
    }

    this.logPopupManagerState('Lock is not empty');
    return false;
  }

  forceAcquireLock(key) {
    if (!this.checkPermission()) {
      this.lock.unshift({ key, force: true });
      this.logPopupManagerState('Force acquired lock without permission');
      return true;
    }

    if (!this.validateKey(key)) {
      return false;
    }

    this.lock.unshift({ key, force: true });
    this.logPopupManagerState('Force acquired lock');
    return true;
  }

  async releaseLock(key) {
    if (!this.checkPermission()) {
      this.lock = _.filter(this.lock, item => item.key !== key);
      this.logPopupManagerState('Released lock without permission');
      return true;
    }

    const idx = _.findIndex(this.lock, item => item.key === key);
    if (idx !== -1) {
      // remove the key from lock
      this.lock = _.filter(this.lock, item => item.key !== key);
      this.logPopupManagerState('Released lock');

      // pop the wait list
      await this.popWaitList();

      return true;
    }

    return false;
  }

  getWaitLockPromise(key, priority) {
    return new Promise((resolve) => {
      this.waitLock({
        key,
        priority,
        callback: async () => {
          resolve(await this.acquireLock({ key }));
        },
      });
    });
  }

  waitLock({
    key,
    priority = priorityLevel.default,
    callback = _.noop,
  }) {
    this.waitList.push({ key, priority, callback });
    this.waitList.sort((a, b) => a.priority - b.priority);
  }

  async popWaitList() {
    if (_.isEmpty(this.lock) && !_.isEmpty(this.waitList)) {
      const { callback } = this.waitList.shift();
      // call the callback to resolve the promise
      await callback();

      return true;
    }

    return false;
  }

  async clearWaitList() {
    await Promise.all(this.waitList.map(async ({ callback }) => {
      // call the callback to reject the promise
      await callback();
    }));
    this.waitList = [];
  }

  getGate() {
    return this.gate;
  }

  setGate() {
    this.gate = true;
  }

  async clearGate() {
    // pop the wait list, and clear the wait list and the gate
    this.logPopupManagerState('Clear gate');
    this.gate = false;
    await this.popWaitList();
    await this.clearWaitList();
    this.logPopupManagerState('After clear gate');
  }

  getTimer() {
    return this.timer;
  }

  setTimer(timeout) {
    if (this.timer) {
      this.clearTimer();
    }

    this.timer = setTimeout(async () => {
      // pop the wait list, and clear the wait list and timer
      this.clearTimer();
      await this.popWaitList();
      await this.clearWaitList();
    }, timeout);
  }

  clearTimer() {
    clearTimeout(this.timer);
    this.timer = null;
  }

  checkPermission() {
    return this.isCentralizedPopupManagementEnabled;
  }

  setPermissions(permissions) {
    if (_.result(permissions, 'IsCentralizedPopupManagementEnabled')) {
      this.isCentralizedPopupManagementEnabled = true;
    } else if (_.result(permissions, 'IsCentralizedPopupManagementEnabled') === false) {
      this.isCentralizedPopupManagementEnabled = false;
    }
  }

  validateKey(key) {
    if (_.isEmpty(key)) {
      return false;
    }

    return true;
  }

  setLogger(logger) {
    this.logger = logger;
  }

  log(message) {
    if (this.logger && _.isFunction(this.logger.signal)) {
      this.logger.signal(message);
    }
  }

  logPopupManagerState(prefix = '') {
    const permissionState = `isCentralizedPopupManagementEnabled: ${this.checkPermission()}`;
    const gateState = `gate: ${this.getGate()}`;
    const lockState = `popups: ${this.lock.length} | system-initiated-popups: ${this.lock.filter(item => item.force === false).length} | user-initiated-popups: ${this.lock.filter(item => item.force === true).length} | popupsKeys: ${this.lock.map(item => item.key)}`;
    const waitListState = `waitListLength: ${this.waitList.length} | waitListKeys: ${this.waitList.map(item => item.key)}`;
    this.log(`${prefix} | ${permissionState} | ${gateState} | ${lockState} | ${waitListState}`);
  }
}

export const popupManager = new PopupManager();

// use for debugging
// @ts-ignore
window.popupManager = popupManager;
